import { Component, createRef } from 'preact';
import { GOLD, TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED, ACCENT_GOLD } from '../lib/medievalTheme';
import { t } from '../localization/t';

const AVATAR_GLYPH = (
  <>
    <path d="M2 17l3-3h4l3 3" /><path d="M9 14V6" /><path d="M12 17l3-3h4l3 3" /><path d="M19 14V6" />
  </>
);

/**
 * Avatar filter shared by the deck collection and the deck picker.
 *
 * The avatar roster runs long, so the menu carries its own search
 * field. Open state stays controlled by the parent because these
 * dropdowns sit in a toolbar with sibling menus that have to close
 * when this one opens.
 *
 * `options` is `[{ value, label, imageUrl }]`.
 */
export default class DeckAvatarFilter extends Component {
  state = { query: '' };
  inputRef = createRef();

  componentDidUpdate(prevProps) {
    // Clear the previous query and hand the caret over the moment the
    // menu opens, so the player can type an avatar name straight away.
    if (!prevProps.open && this.props.open) {
      this.setState({ query: '' });
      this.inputRef.current?.focus();
    }
  }

  renderRow(label, value, imageUrl, isActive) {
    return (
      <button
        key={value ?? '__all'}
        type="button"
        className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left transition-all"
        style={{ color: isActive ? ACCENT_GOLD : TEXT_BODY }}
        onMouseEnter={(e) => { e.currentTarget.style.background = `${GOLD} 0.08)`; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
        onClick={() => this.props.onChange(value)}
      >
        {imageUrl ? (
          <img src={imageUrl} alt="" loading="lazy" className="w-6 h-6 rounded object-cover object-top shrink-0" />
        ) : (
          <span
            className="w-6 h-6 rounded flex items-center justify-center shrink-0"
            style={{ background: `${GOLD} 0.08)`, border: `1px solid ${GOLD} 0.18)` }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ width: '0.75cqw', height: '0.75cqw', color: `${GOLD} 0.5)` }}>
              {AVATAR_GLYPH}
            </svg>
          </span>
        )}
        <span className="truncate">{label}</span>
      </button>
    );
  }

  render() {
    const { options, value, open, onToggle } = this.props;
    const { query } = this.state;
    const q = query.trim().toLowerCase();
    const shown = q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;

    return (
      <div className="relative shrink-0" style={{ borderLeft: `1px solid ${GOLD} 0.12)` }}>
        <button
          type="button"
          className="flex items-center gap-2 px-3 py-2 text-xs transition-all"
          style={{ color: value ? TEXT_PRIMARY : TEXT_MUTED }}
          onClick={onToggle}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: '0.875cqw', height: '0.875cqw' }}>
            {AVATAR_GLYPH}
          </svg>
          {value ? (options.find((o) => o.value === value)?.label || t('Avatar')) : t('Any Avatar')}
        </button>
        {open ? (
          <div
            className="absolute right-0 top-full mt-1 z-50 rounded-xl overflow-hidden"
            style={{
              background: '#0e0b08',
              border: `1px solid ${GOLD} 0.2)`,
              boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
              minWidth: '13cqw',
            }}
          >
            {/* Search sits inside the menu rather than replacing the
                trigger label, so the current selection stays readable
                in the toolbar while the player filters. */}
            <div className="flex items-center px-2.5 py-1.5" style={{ borderBottom: `1px solid ${GOLD} 0.12)` }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0" style={{ width: '0.75cqw', height: '0.75cqw', color: TEXT_MUTED }}>
                <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                ref={this.inputRef}
                type="text"
                placeholder={t('Search avatars...')}
                value={query}
                onInput={(e) => this.setState({ query: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Escape') onToggle(); }}
                className="flex-1 min-w-0 px-2 py-1 text-xs outline-none"
                style={{ background: 'transparent', color: TEXT_BODY, border: 'none' }}
              />
            </div>
            <div className="max-h-60 overflow-y-auto medieval-scrollbar">
              {!q ? this.renderRow(t('Any Avatar'), null, null, !value) : null}
              {shown.map((o) => this.renderRow(o.label, o.value, o.imageUrl, value === o.value))}
              {shown.length === 0 ? (
                <div className="px-3 py-2 text-xs italic" style={{ color: TEXT_MUTED }}>{t('No avatars match.')}</div>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    );
  }
}
